import { Box, Flex } from '@mantine/core'

import { isBudgetCategory } from '@/types/budgets.types'

import { BudgetDonutChart } from '@/components/BudgetDonutChart/BudgetDonutChart'
import type { BudgetsListItemProps } from '@/components/BudgetsListItem/BudgetsListItem.types'
import { MoreDropdown } from '@/components/MoreDropdown/MoreDropdown'
import { Text } from '@/components/Text/Text'

import { BUDGET_CATEGORIES } from '@/constants/budgetCategories.constants'

import { formatMoney } from '@/helpers/formatMoney.helper'
import { mapCategories } from '@/helpers/mapCategories.helper'
import { sanitizeEnum } from '@/helpers/sanitizeEnum.helper'

import classes from './BudgetsListItem.module.css'

export function BudgetsListItem({
  budget,
  openDeleteModal,
  openEditModal,
  readOnly,
}: BudgetsListItemProps) {
  const currentCategory = BUDGET_CATEGORIES.find(
    category => category.name === budget.category,
  )

  if (!currentCategory) {
    return null
  }

  const percentageSpent = +(
    (budget.spent / budget.monthlyAmount) *
    100
  ).toFixed(2)

  const chartData = [
    {
      color: currentCategory.color,
      name: 'Spent',
      value: percentageSpent > 100 ? 100 : percentageSpent,
    },
    {
      color: 'var(--mantine-other-colors-light-grey)',
      name: 'Remaining',
      value: percentageSpent > 100 ? 0 : 100 - percentageSpent,
    },
  ]

  return (
    <Flex className={classes.container}>
      <Flex align="center" gap={8}>
        <Box className={classes.chart}>
          <BudgetDonutChart data={chartData} />

          <currentCategory.icon className={classes.categoryIcon} />
        </Box>

        <Box>
          <Text className={classes.categoryName} variant="body_2_medium_black">
            {isBudgetCategory(budget.category)
              ? mapCategories(budget.category)
              : sanitizeEnum(budget.category)}
          </Text>

          <Text lineClamp={1} variant="body_4_gray">
            {formatMoney(budget.spent, 'USD')} Spent
          </Text>

          <Text lineClamp={1} variant="body_4_gray">
            {formatMoney(budget.remaining, 'USD')}{' '}
            {budget.remaining > 0 ? 'Remaining' : 'Over Budget'}
          </Text>
        </Box>
      </Flex>

      <Flex>
        <Text lineClamp={1} mr={8} variant="body_1_semibold">
          {formatMoney(+budget.monthlyAmount, 'USD')}
        </Text>

        {!readOnly && openEditModal && openDeleteModal && (
          <MoreDropdown
            items={[
              {
                label: 'Edit',
                onClick: openEditModal,
                param: budget.id.toString(),
              },
              {
                label: 'Delete',
                onClick: openDeleteModal,
                param: budget.id.toString(),
              },
            ]}
          />
        )}
      </Flex>
    </Flex>
  )
}
